import { Fragment, useEffect, useRef, useState } from 'react'
import { BarChart3, ChevronDown, ChevronRight, DollarSign, FileText, RefreshCw, Search, Users } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { MiniCalendarioMarcacoes } from '../componentes/MiniCalendarioMarcacoes'
import { Lote } from '../tipos/ajudaCusto'
import { useToast } from '../../../contexts/NotificationContext'
import { ajudaCustoService } from '../services/ajudaCusto'
import { exportarPreviaPagamentoPdf } from '../services/exportarPreviaPagamentoPdf'
import { useLotes, useGerarLote, usePessoasAjudaCusto } from '../hooks/useAjudaCusto'
import { RelatorioPreview } from '../tipos/ajudaCusto'
import { formatarData, formatarDataHora, formatarMoeda } from '../utils'
import { Search as SearchIcon } from 'lucide-react'
import SeletorPeriodo from '../../../componentesCompartilhados/periodo/SeletorPeriodo'
import { usePeriodo } from '../hooks/usePeriodo'

function CardResumo({ icon, label, valor }: { icon: React.ReactNode; label: string; valor: string }) {
  return (
    <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 p-4 flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{valor}</p>
      </div>
    </div>
  )
}

export default function Relatorio() {
  const toast = useToast()
  const { data: lotesResultado, isLoading: loadingLotes, refetch, isFetching } = useLotes()
  const { data: pessoas = [] } = usePessoasAjudaCusto()
  const gerarLote = useGerarLote()

  const periodo = usePeriodo('ultimos30')
  const [pessoaId, setPessoaId] = useState('')

  const [preview, setPreview] = useState<RelatorioPreview | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [expandido, setExpandido] = useState<Set<number>>(new Set())
  const [loteDetalhe, setLoteDetalhe] = useState<Lote | null>(null)
  const [expandidoLote, setExpandidoLote] = useState<Set<number>>(new Set())

  const toggleExpandidoLote = (pessoaId: number) => {
    setExpandidoLote((prev) => {
      const next = new Set(prev)
      if (next.has(pessoaId)) next.delete(pessoaId)
      else next.add(pessoaId)
      return next
    })
  }

  const toggleExpandido = (pessoaId: number) => {
    setExpandido((prev) => {
      const next = new Set(prev)
      if (next.has(pessoaId)) next.delete(pessoaId)
      else next.add(pessoaId)
      return next
    })
  }

  const resumo = lotesResultado?.resumo
  const lotes = lotesResultado?.lotes ?? []

  const LOTES_POR_PAGINA = 10
  const [paginaLotes, setPaginaLotes] = useState(1)
  const totalPaginasLotes = Math.max(1, Math.ceil(lotes.length / LOTES_POR_PAGINA))
  const paginaAtual = Math.min(paginaLotes, totalPaginasLotes)
  const lotesPagina = lotes.slice((paginaAtual - 1) * LOTES_POR_PAGINA, paginaAtual * LOTES_POR_PAGINA)

  const buscar = async () => {
    const { dataInicio, dataFim } = periodo.resolver()
    if (!dataInicio || !dataFim) {
      toast.warning('Informe o período.')
      return
    }
    setBuscando(true)
    try {
      const result = await ajudaCustoService.buscarRelatorio(dataInicio, dataFim, pessoaId ? Number(pessoaId) : undefined)
      setPreview(result)
    } catch (e: any) {
      toast.error(e?.response?.data || e?.message || 'Erro ao buscar')
    } finally {
      setBuscando(false)
    }
  }

  // Ao aplicar o período, refaz a busca automaticamente (pula o primeiro render).
  const primeiraBusca = useRef(true)
  useEffect(() => {
    if (primeiraBusca.current) {
      primeiraBusca.current = false
      return
    }
    buscar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo.valores.tipoPeriodo, periodo.valores.dataReferencia, periodo.valores.intervaloInicio, periodo.valores.intervaloFim])

  const gerar = async () => {
    if (!preview) return
    await gerarLote.mutateAsync({
      dataInicio: preview.dataInicio,
      dataFim: preview.dataFim,
      pessoaId: pessoaId ? Number(pessoaId) : undefined,
    })
    exportarPreviaPagamentoPdf(preview)
    setPreview(null)
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bônus Presencial — Relatório</h1>
        <p className="text-gray-600">Gere lotes de pagamento e acompanhe o histórico.</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <CardResumo icon={<FileText className="h-5 w-5" />} label="Lotes pagos" valor={String(resumo?.totalLotes ?? 0)} />
        <CardResumo icon={<DollarSign className="h-5 w-5" />} label="Total pago" valor={formatarMoeda(resumo?.valorTotalPago ?? 0)} />
        <CardResumo icon={<Users className="h-5 w-5" />} label="Pessoas" valor={String(resumo?.totalPessoas ?? 0)} />
        <CardResumo icon={<BarChart3 className="h-5 w-5" />} label="Dias" valor={String(resumo?.totalDias ?? 0)} />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Período</label>
            <SeletorPeriodo {...periodo.seletorProps} />
          </div>
          <div className="min-w-[12rem]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Pessoa</label>
            <select value={pessoaId} onChange={(e) => setPessoaId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Todas</option>
              {pessoas.map((p) => (
                <option key={p.idPessoa} value={p.idPessoa}>{p.nome}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={buscar}
            disabled={buscando}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Lotes */}
      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-gray-800 font-semibold">
            <FileText className="h-5 w-5 text-brand-500" />
            Lotes pagos
          </div>
          <button type="button" onClick={() => refetch()} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Período</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Gerado em</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Pessoas</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Dias</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor total</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingLotes ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Carregando...</td></tr>
              ) : lotes.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nenhum lote gerado ainda.</td></tr>
              ) : (
                lotesPagina.map((l) => (
                  <tr key={l.idLotePagamentoAjudaCusto} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{formatarData(l.dataInicioPeriodo)} — {formatarData(l.dataFimPeriodo)}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{formatarDataHora(l.dataGeracao)}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{l.quantidadePessoas}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{l.quantidadeDias}</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">{formatarMoeda(l.valorTotal)}</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => setLoteDetalhe(l)}
                        title="Ver pessoas do lote"
                        className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-md"
                      >
                        <SearchIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {lotes.length > LOTES_POR_PAGINA && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              {(paginaAtual - 1) * LOTES_POR_PAGINA + 1}–{Math.min(paginaAtual * LOTES_POR_PAGINA, lotes.length)} de {lotes.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={paginaAtual <= 1}
                onClick={() => setPaginaLotes((p) => Math.max(1, p - 1))}
                className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="text-xs text-gray-600">{paginaAtual}/{totalPaginasLotes}</span>
              <button
                type="button"
                disabled={paginaAtual >= totalPaginasLotes}
                onClick={() => setPaginaLotes((p) => Math.min(totalPaginasLotes, p + 1))}
                className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal preview */}
      <Modal
        isOpen={!!preview}
        onClose={() => setPreview(null)}
        title="Prévia de pagamento"
        subtitle={preview ? `${formatarData(preview.dataInicio)} — ${formatarData(preview.dataFim)}` : undefined}
        icon={<DollarSign className="h-6 w-6 text-brand-500" />}
        size="2xl"
        footer={
          <div className="flex items-center justify-between p-4">
            <span className="text-sm text-gray-600">
              Total: <span className="font-bold text-gray-900">{formatarMoeda(preview?.valorTotalGeral ?? 0)}</span>
            </span>
            <div className="flex gap-3">
              <button type="button" onClick={() => setPreview(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button
                type="button"
                disabled={!preview || preview.pessoas.length === 0 || (preview.datasSemVigencia?.length ?? 0) > 0 || gerarLote.isLoading}
                onClick={gerar}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
              >
                Gerar pagamento
              </button>
            </div>
          </div>
        }
      >
        {preview && (
          <div className="space-y-4">
            {preview.datasSemVigencia?.length > 0 && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                Existem dias sem valor de diária parametrizado ({preview.datasSemVigencia.map(formatarData).join(', ')}). Cadastre a parametrização antes de gerar.
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pessoa</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Dias</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {preview.pessoas.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">Nenhum registro a pagar no período.</td></tr>
                ) : (
                  preview.pessoas.map((p) => {
                    const aberto = expandido.has(p.pessoaId)
                    return (
                      <Fragment key={p.pessoaId}>
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpandido(p.pessoaId)}>
                          <td className="px-4 py-2 text-gray-900 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              {aberto ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                              {p.pessoaNome}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center text-gray-600">{p.quantidadeDias}</td>
                          <td className="px-4 py-2 text-right font-medium text-gray-900">{formatarMoeda(p.valorTotal)}</td>
                        </tr>
                        {aberto && (
                          <tr>
                            <td colSpan={3} className="px-4 pb-3 bg-gray-50/60">
                              <MiniCalendarioMarcacoes datas={p.datas} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Modal detalhes do lote */}
      <Modal
        isOpen={!!loteDetalhe}
        onClose={() => setLoteDetalhe(null)}
        title="Detalhes do lote"
        subtitle={loteDetalhe ? `${formatarData(loteDetalhe.dataInicioPeriodo)} — ${formatarData(loteDetalhe.dataFimPeriodo)} · gerado em ${formatarDataHora(loteDetalhe.dataGeracao)}` : undefined}
        icon={<FileText className="h-6 w-6 text-brand-500" />}
        size="2xl"
        footer={
          <div className="flex items-center justify-between p-4">
            <span className="text-sm text-gray-600">
              Total: <span className="font-bold text-gray-900">{formatarMoeda(loteDetalhe?.valorTotal ?? 0)}</span>
            </span>
            <button type="button" onClick={() => setLoteDetalhe(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Fechar</button>
          </div>
        }
      >
        {loteDetalhe && (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pessoa</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Dias</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loteDetalhe.itens.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">Sem itens.</td></tr>
              ) : (
                loteDetalhe.itens.map((i) => {
                  const aberto = expandidoLote.has(i.pessoaId)
                  return (
                    <Fragment key={i.pessoaId}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpandidoLote(i.pessoaId)}>
                        <td className="px-4 py-2 text-gray-900 font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {aberto ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            {i.pessoaNome ?? `#${i.pessoaId}`}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center text-gray-600">{i.quantidadeDias}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900">{formatarMoeda(i.valorTotalPessoa)}</td>
                      </tr>
                      {aberto && (
                        <tr>
                          <td colSpan={3} className="px-4 pb-3 bg-gray-50/60">
                            <MiniCalendarioMarcacoes datas={i.datas} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  )
}
